// UI state management factory function

import { logger } from "../utils/logger.ts";
import {
  ensureSubtoolForMode,
  getModeForPrimaryTool,
  getModeForSubtool,
  getPrimaryToolForMode,
} from "./tool-mode-map";
import type {
  UIStateSettings,
  UIStateAPI,
  UIStateConfig,
  ZoomMode,
  BreakpointType,
  Theme,
  ToolName,
  SubtoolName,
  Helper,
  ToolMode,
  // Point,
  DragPosition,
  UndoRedoAction,
  Language,
} from "../types/user-types";

type Mutable<T> = {
  -readonly [K in keyof T]: MutableValue<T[K]>;
};

type MutableValue<T> = T extends readonly (infer U)[]
  ? MutableValue<U>[]
  : T extends Function
  ? T
  : T extends object
  ? Mutable<T>
  : T;

type MutableUIState = Mutable<UIStateSettings>;

// Default values
// const DEFAULT_DRAG_POSITION: DragPosition = {
//   current: { x: 0, y: 0 },
//   start: { x: 0, y: 0 },
//   delta: { x: 0, y: 0 },
// };

const DEFAULT_UI_STATE: UIStateSettings = {
  zoom_value: 1.0,
  zoom_mode: "zoom_all" as ZoomMode,
  stage_position: { x: 0, y: 0 },
  current_breakpoint: "desktop" as BreakpointType,
  current_language: "en" as Language,
  theme: "light" as Theme,
  drag_current_position: null,
  current_project: "Untitled Project",
  current_page: "Page 1",
  current_selection: [],
  selected_tool: "select" as ToolName,
  selected_subtool: null,
  selectedMode: "edit" as any,
  selectedTool: null,
  sketchEraserMode: "delete",
  sketchSelectionMode: "marquee",
  helpers: ["grid"] as readonly Helper[],
  undo_redo_stack: [],
  show_all: {
    edit: false,
    sketch: false,
  },
  // Temporary states - don't persist to localStorage
  shift_pressed: false,
  alt_pressed: false,
  mic_enabled: false,
  llm_working: false,
  pencil_connected: false,
  audio_muted: false,
};

/**
 * Creates a UI state manager with undo/redo functionality
 *
 * @param config - Configuration options for UI state management
 * @returns UI state API with state management methods
 *
 * @example
 * ```typescript
 * const uiState = createUIState({
 *   maxUndoStackSize: 50,
 *   onToolChange: (tool, subtool) => console.log('Tool changed:', tool, subtool),
 *   onSelectionChange: (selection) => console.log('Selection:', selection)
 * });
 *
 * uiState.setTool('draw', 'ballpoint');
 * uiState.setSelection(['element-1', 'element-2']);
 * uiState.destroy(); // Always call when done
 * ```
 */
export const createUIState = (config: UIStateConfig = {}): UIStateAPI => {
  const {
    initialState = {},
    onStateChange,
    maxUndoStackSize = 100,
    onToolChange,
    onSelectionChange,
    autoSave = false,
    storageKey = "sketch-app-ui-state",
  } = config;

  // Merge initial state with defaults
  let state: UIStateSettings = {
    ...DEFAULT_UI_STATE,
    ...initialState,
    helpers: [...(initialState.helpers || DEFAULT_UI_STATE.helpers)],
    current_selection: [
      ...(initialState.current_selection || DEFAULT_UI_STATE.current_selection),
    ],
    undo_redo_stack: [
      ...(initialState.undo_redo_stack || DEFAULT_UI_STATE.undo_redo_stack),
    ],
    selectedMode:
      (initialState as any)?.selectedMode ||
      (initialState.selected_tool === "draw"
        ? ("sketch" as any)
        : (initialState.selected_tool as any) || "edit"),
    selectedTool:
      (initialState as any)?.selectedTool ??
      initialState.selected_subtool ??
      null,
    show_all: {
      edit:
        (initialState as any)?.show_all?.edit ??
        DEFAULT_UI_STATE.show_all.edit,
      sketch:
        (initialState as any)?.show_all?.sketch ??
        DEFAULT_UI_STATE.show_all.sketch,
    },
  };

  // Load from localStorage if available
  if (typeof window !== "undefined" && autoSave) {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        logger.debug(
          "persistence",
          "📦 Loading UI state from localStorage",
          parsed
        );

        // Merge stored state with current state, preserving arrays structure
        state = {
          ...state,
          ...parsed,
          helpers: [...(parsed.helpers || state.helpers)],
          current_selection: [
            ...(parsed.current_selection || state.current_selection),
          ],
          show_all: {
            edit:
              (parsed as any)?.show_all?.edit ??
              state.show_all.edit,
            sketch:
              (parsed as any)?.show_all?.sketch ??
              state.show_all.sketch,
          },
          // Don't restore undo/redo stack or temporary states - start fresh each session
          undo_redo_stack: [],
          shift_pressed: false,
          alt_pressed: false,
          mic_enabled: false,
          llm_working: false,
          pencil_connected: false,
        };

        logger.debug("persistence", "✅ UI state loaded from localStorage");
      }
    } catch (error) {
      logger.warn(
        "persistence",
        "⚠️ Failed to load UI state from localStorage",
        error
      );
    }
  }

  let redoStack: UndoRedoAction[] = [];

  // Helper to generate unique IDs
  const generateId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  // Helper to trigger state change notifications
  const notifyStateChange = () => {
    onStateChange?.(state);

    // Save to localStorage if enabled
    if (autoSave && typeof window !== "undefined") {
      try {
        // Create a copy without undo/redo stack and temporary states for storage
        const stateToSave = {
          ...state,
          undo_redo_stack: [], // Don't persist undo stack
          // Exclude temporary states from persistence
          shift_pressed: undefined,
          alt_pressed: undefined,
          mic_enabled: undefined,
          llm_working: undefined,
          // audio_muted is persisted (project pref), keep it
        };

        localStorage.setItem(storageKey, JSON.stringify(stateToSave));
        logger.trace("persistence", "💾 UI state saved to localStorage");
      } catch (error) {
        logger.warn(
          "persistence",
          "⚠️ Failed to save UI state to localStorage",
          error
        );
      }
    }
  };

  // Helper to manage undo stack size
  const trimUndoStack = () => {
    if (state.undo_redo_stack.length > maxUndoStackSize) {
      state = {
        ...state,
        undo_redo_stack: state.undo_redo_stack.slice(-maxUndoStackSize),
      };
    }
  };

  // Public API
  const getState = (): UIStateSettings => ({
    ...state,
    helpers: [...state.helpers],
    current_selection: [...state.current_selection],
    undo_redo_stack: [...state.undo_redo_stack],
  });

  const setZoom = (value: number, mode: ZoomMode = "custom") => {
    const clampedValue = Math.max(0.1, Math.min(10, value));
    state = {
      ...state,
      zoom_value: clampedValue,
      zoom_mode: mode,
    };
    notifyStateChange();
  };

  const setZoomMode = (mode: ZoomMode) => {
    state = {
      ...state,
      zoom_mode: mode,
    };
    notifyStateChange();
  };

  const setStagePosition = (x: number, y: number) => {
    state = {
      ...state,
      stage_position: { x, y },
    };
    notifyStateChange();
  };

  const setBreakpoint = (breakpoint: BreakpointType) => {
    state = {
      ...state,
      current_breakpoint: breakpoint,
    };
    notifyStateChange();
  };

  const setTheme = (theme: Theme) => {
    state = {
      ...state,
      theme,
    };
    notifyStateChange();
  };

  const setLanguage = (language: Language) => {
    state = {
      ...state,
      current_language: language,
    };
    notifyStateChange();
  };

  const setDragPosition = (position: DragPosition | null) => {
    state = {
      ...state,
      drag_current_position: position
        ? {
            current: { ...position.current },
            start: { ...position.start },
            delta: { ...position.delta },
          }
        : null,
    };
    notifyStateChange();
  };

  const setProject = (project: string) => {
    state = {
      ...state,
      current_project: project,
    };
    notifyStateChange();
  };

  const setPage = (page: string) => {
    state = {
      ...state,
      current_page: page,
    };
    notifyStateChange();
  };

  const setShowAll = (
    mode: Extract<ToolMode, "edit" | "sketch">,
    value: boolean
  ) => {
    if (state.show_all[mode] === value) return;
    state = {
      ...state,
      show_all: {
        ...state.show_all,
        [mode]: value,
      },
    };
    notifyStateChange();
  };

  const setAudioMuted = (muted: boolean) => {
    state = {
      ...state,
      audio_muted: !!muted,
    };
    notifyStateChange();
  };

  const setSelection = (selection: readonly string[]) => {
    state = {
      ...state,
      current_selection: [...selection],
    };
    onSelectionChange?.(state.current_selection);
    notifyStateChange();
  };

  const setHelpers = (helpers: readonly Helper[]) => {
    // Normalize and de-duplicate helpers
    const set = new Set<Helper>(helpers as Helper[]);
    state = {
      ...state,
      helpers: Array.from(set),
    } as UIStateSettings;
    notifyStateChange();
  };

  const addToSelection = (id: string) => {
    if (!state.current_selection.includes(id)) {
      state = {
        ...state,
        current_selection: [...state.current_selection, id],
      };
      onSelectionChange?.(state.current_selection);
      notifyStateChange();
    }
  };

  const removeFromSelection = (id: string) => {
    state = {
      ...state,
      current_selection: state.current_selection.filter((item) => item !== id),
    };
    onSelectionChange?.(state.current_selection);
    notifyStateChange();
  };

  const clearSelection = () => {
    state = {
      ...state,
      current_selection: [],
    };
    onSelectionChange?.(state.current_selection);
    notifyStateChange();
  };

  const setTool = (tool: ToolName, subtool: SubtoolName | null = null) => {
    const currentMode = state.selectedMode as ToolMode | undefined;
    const mappedMode = getModeForPrimaryTool(tool) as ToolMode | null;
    const isDedicatedMode =
      currentMode === ("ds" as any) || currentMode === ("cms" as any);
    let nextMode: ToolMode | string;

    if (tool === ("draw" as any)) {
      nextMode = "sketch" as ToolMode;
    } else if (
      isDedicatedMode &&
      (!subtool || typeof subtool !== "string") &&
      mappedMode === ("edit" as any)
    ) {
      nextMode = currentMode ?? (mappedMode || (tool as any));
    } else {
      nextMode = (mappedMode as any) || (tool as any);
    }

    const nextState: UIStateSettings & {
      selected_tool: ToolName;
      selected_subtool: SubtoolName | null;
    } = {
      ...state,
      selected_tool: tool,
      selected_subtool: subtool,
      selectedMode: nextMode,
      selectedTool: subtool,
    } as any;
    if (subtool === "delete" || subtool === "delete-clean") {
      nextState.sketchEraserMode = subtool;
    }
    if (subtool === "marquee" || subtool === "lasso") {
      nextState.sketchSelectionMode = subtool;
    }
    state = nextState;
    onToolChange?.(tool, subtool ?? null, nextMode as any);
    notifyStateChange();
  };

  // Aliases with semantically clearer naming (non-breaking additions)
  const setMode = (mode: string) => {
    try {
      const normalizedMode = mode as any;
      if (normalizedMode === "ds" || normalizedMode === "cms") {
        const nextState = {
          ...state,
          selectedMode: normalizedMode,
          selected_tool: null,
          selected_subtool: null,
          selectedTool: null,
        } as UIStateSettings & { selected_tool: any };
        const hasChanged =
          nextState.selectedMode !== state.selectedMode ||
          nextState.selected_tool !== state.selected_tool ||
          nextState.selected_subtool !== state.selected_subtool ||
          nextState.selectedTool !== state.selectedTool;

        state = nextState;
        // Always emit tool change so listeners (e.g., sidebar) can reapply
        // the non-tool modes even when the persisted state already matches.
        onToolChange?.(null as any, null, normalizedMode as any);

        if (hasChanged) {
          notifyStateChange();
        }
        return;
      }

      const preferredSubtool =
        typeof state.selectedTool === "string"
          ? (state.selectedTool as SubtoolName)
          : (state.selected_subtool as SubtoolName | null);
      const nextSubtool = ensureSubtoolForMode(
        normalizedMode,
        preferredSubtool
      );
      const mappedTool = getPrimaryToolForMode(normalizedMode) as ToolName;
      setTool(mappedTool, nextSubtool);
    } catch {
      // no-op on invalid values
    }
  };

  const setToolSub = (subtool: SubtoolName | null) => {
    const desired = subtool ?? null;
    const detectedMode = getModeForSubtool(desired);
    const fallbackMode = state.selectedMode as any;
    const effectiveMode = (detectedMode || fallbackMode || "edit") as any;

    if (effectiveMode === "ds" || effectiveMode === "cms") {
      const nextState = {
        ...state,
        selectedMode: effectiveMode,
        selected_subtool: null,
        selectedTool: null,
        selected_tool: null,
      } as UIStateSettings & { selected_tool: any };
      state = nextState;
      onToolChange?.(null as any, null, effectiveMode);
      notifyStateChange();
      return;
    }

    const normalized = ensureSubtoolForMode(effectiveMode, desired);
    const mappedTool = getPrimaryToolForMode(effectiveMode) as ToolName;
    setTool(mappedTool, normalized);
  };

  const toggleHelper = (helper: Helper) => {
    const isActive = state.helpers.includes(helper);
    state = {
      ...state,
      helpers: isActive
        ? state.helpers.filter((h) => h !== helper)
        : [...state.helpers, helper],
    };
    notifyStateChange();
  };

  const addToUndoStack = (action: Omit<UndoRedoAction, "id" | "timestamp">) => {
    const undoAction: UndoRedoAction = {
      ...action,
      id: generateId(),
      timestamp: new Date(),
    };

    state = {
      ...state,
      undo_redo_stack: [...state.undo_redo_stack, undoAction],
    };

    // Clear redo stack when new action is added
    redoStack = [];

    trimUndoStack();
    notifyStateChange();
  };

  const undo = (): UndoRedoAction | null => {
    if (state.undo_redo_stack.length === 0) {
      return null;
    }

    const lastAction = state.undo_redo_stack[state.undo_redo_stack.length - 1]!;

    state = {
      ...state,
      undo_redo_stack: state.undo_redo_stack.slice(0, -1),
    };

    redoStack.push(lastAction);

    notifyStateChange();
    return lastAction;
  };

  const redo = (): UndoRedoAction | null => {
    if (redoStack.length === 0) {
      return null;
    }

    const actionToRedo = redoStack.pop()!;

    state = {
      ...state,
      undo_redo_stack: [...state.undo_redo_stack, actionToRedo],
    };

    notifyStateChange();
    return actionToRedo;
  };

  const clearUndoStack = () => {
    state = {
      ...state,
      undo_redo_stack: [],
    };
    redoStack = [];
    notifyStateChange();
  };

  const reset = () => {
    state = { ...DEFAULT_UI_STATE };
    redoStack = [];
    notifyStateChange();
  };

  const applyState = (patch: Partial<UIStateSettings>) => {
    if (!patch || typeof patch !== "object") return;
    const entries = Object.entries(patch) as [keyof UIStateSettings, UIStateSettings[keyof UIStateSettings]][];
    if (!entries.length) return;

    const next: MutableUIState = { ...state } as MutableUIState;
    let changed = false;

    const updateArray = <T>(
      value: readonly T[] | undefined,
      assign: (nextValue: T[]) => void
    ) => {
      if (!value) return;
      const nextValue = [...value];
      assign(nextValue);
      changed = true;
    };

    let selectionTouched = false;
    let explicitModeProvided = false;
    for (const [key, value] of entries) {
      if (typeof value === "undefined") continue;
      switch (key) {
        case "helpers": {
          updateArray(value as readonly Helper[], (v) => {
            next.helpers = v;
          });
          break;
        }
        case "current_selection": {
          updateArray(value as readonly string[], (v) => {
            next.current_selection = v;
          });
          break;
        }
        case "undo_redo_stack": {
          updateArray(value as readonly UndoRedoAction[], (v) => {
            next.undo_redo_stack = v;
          });
          break;
        }
        case "stage_position": {
          const pos = value as UIStateSettings["stage_position"];
          if (
            pos &&
            typeof pos.x === "number" &&
            typeof pos.y === "number" &&
            (next.stage_position.x !== pos.x || next.stage_position.y !== pos.y)
          ) {
            next.stage_position = { x: pos.x, y: pos.y };
            changed = true;
          }
          break;
        }
        case "show_all": {
          const payload = value as Partial<UIStateSettings["show_all"]>;
          if (payload && typeof payload === "object") {
            const nextEdit =
              typeof payload.edit === "boolean"
                ? payload.edit
                : next.show_all.edit;
            const nextSketch =
              typeof payload.sketch === "boolean"
                ? payload.sketch
                : next.show_all.sketch;
            if (
              nextEdit !== next.show_all.edit ||
              nextSketch !== next.show_all.sketch
            ) {
              next.show_all = {
                edit: nextEdit,
                sketch: nextSketch,
              };
              changed = true;
            }
          }
          break;
        }
        default: {
          if ((next as any)[key] !== value) {
            (next as any)[key] = value;
            changed = true;
            if (
              key === "selectedMode" ||
              key === "selectedTool" ||
              key === "selected_subtool" ||
              key === "selected_tool"
            ) {
              selectionTouched = true;
              if (key === "selectedMode") explicitModeProvided = true;
            }
          }
        }
      }
    }

    if (selectionTouched) {
      const desiredSubtool = (next.selectedTool ?? next.selected_subtool) as
        | SubtoolName
        | null;
      let effectiveMode =
        typeof next.selectedMode === "string"
          ? (next.selectedMode as any)
          : null;

      if (!explicitModeProvided) {
        const inferred = getModeForSubtool(desiredSubtool);
        if (inferred && inferred !== effectiveMode) {
          next.selectedMode = inferred as any;
          effectiveMode = inferred as any;
          changed = true;
        }
      }

      if (!effectiveMode) {
        next.selectedMode = "edit" as any;
        effectiveMode = "edit";
        changed = true;
      }

      const normalizedSubtool = ensureSubtoolForMode(
        effectiveMode,
        desiredSubtool
      );
      if (normalizedSubtool !== next.selectedTool) {
        next.selectedTool = normalizedSubtool;
        changed = true;
      }
      if (normalizedSubtool !== next.selected_subtool) {
        next.selected_subtool = normalizedSubtool;
        changed = true;
      }
      const mappedTool = getPrimaryToolForMode(effectiveMode as any);
      if (effectiveMode === "ds" || effectiveMode === "cms") {
        if ((next as any).selected_tool !== null) {
          (next as any).selected_tool = null;
          changed = true;
        }
      } else if ((next as any).selected_tool !== mappedTool) {
        (next as any).selected_tool = mappedTool as any;
        changed = true;
      }
    }

    if (!changed) return;
    state = next;
    notifyStateChange();
  };

  // Temporary state setters (don't persist to localStorage)
  const setShiftPressed = (pressed: boolean) => {
    state = {
      ...state,
      shift_pressed: pressed,
    };
    notifyStateChange();
  };

  const setAltPressed = (pressed: boolean) => {
    state = {
      ...state,
      alt_pressed: pressed,
    };
    notifyStateChange();
  };

  const setMicEnabled = (enabled: boolean) => {
    state = {
      ...state,
      mic_enabled: enabled,
    };
    notifyStateChange();
  };

  const setLLMWorking = (working: boolean) => {
    state = {
      ...state,
      llm_working: working,
    };
    notifyStateChange();
  };

  const setPencilConnected = (connected: boolean) => {
    state = {
      ...state,
      pencil_connected: connected,
    };
    notifyStateChange();
  };

  const destroy = () => {
    // Cleanup any resources if needed
    redoStack = [];
  };

  return {
    getState,
    setZoom,
    setZoomMode,
    setStagePosition,
    setBreakpoint,
    setTheme,
    setLanguage,
    setDragPosition,
    setProject,
    setPage,
    setSelection,
    setHelpers,
    setShowAll,
    setAudioMuted,
    addToSelection,
    removeFromSelection,
    clearSelection,
    setTool,
    setMode,
    setToolSub,
    toggleHelper,
    addToUndoStack,
    undo,
    redo,
    clearUndoStack,
    reset,
    applyState,
    destroy,
    // Temporary state methods
    setShiftPressed,
    setAltPressed,
    setMicEnabled,
    setLLMWorking,
    setPencilConnected,
  };
};
